import { memo, useCallback, useState } from 'react';

import { CheckIcon, XIcon } from '@air/design-icons';
import { Chip } from '@air/primitive-chip';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxInputTrigger,
  ComboboxItem,
  ComboboxList,
  ComboboxLoading,
} from '@air/primitive-combobox';
import { IconButton } from '@air/primitive-icon-button';
import { cn } from '@air/utils-tailwind';

export type BaseStringSelectProps = {
  id?: string;
  isLoading?: boolean;
  isSingleSelect?: boolean;
  placeholder?: string;
  selectedValues: string[];
  onInputChange?: (value: string) => void;
  onSelectionChange: (newValues: string[]) => void;
};

type StringSelectProps = {
  values: string[];
} & BaseStringSelectProps;

export const StringSelect = memo(
  ({
    id,
    placeholder,
    values,
    selectedValues,
    onSelectionChange,
    isLoading,
    onInputChange,
    isSingleSelect,
  }: StringSelectProps) => {
    const [searchValue, setSearchValue] = useState('');

    const onRemoveValue = useCallback(
      (value: string) => {
        onSelectionChange(selectedValues.filter((val) => val !== value));
      },
      [onSelectionChange, selectedValues],
    );

    const onValueClick = useCallback(
      (value: string) => {
        if (selectedValues.some((val) => val === value)) {
          onRemoveValue(value);
        } else {
          if (isSingleSelect) {
            onSelectionChange([value]);
          } else {
            onSelectionChange([...selectedValues, value]);
          }
        }
      },
      [isSingleSelect, onRemoveValue, onSelectionChange, selectedValues],
    );

    const onValueChange = useCallback(
      (value: string) => {
        setSearchValue(value);
        onInputChange?.(value);
      },
      [onInputChange],
    );

    return (
      <Combobox>
        <ComboboxInputTrigger data-id={id}>
          {selectedValues.length > 0 ? (
            selectedValues.map((value) => (
              <Chip data-id={value} key={value} shape="rounded" size="medium">
                <span className="truncate">{value}</span>
                <IconButton
                  adsIcon
                  appearance="ghost"
                  color="gray"
                  data-testid="REMOVE_TAG_BUTTON"
                  data-id={value}
                  icon={XIcon}
                  label="Remove smart tag"
                  // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
                  onClick={(event) => {
                    event.stopPropagation();

                    onRemoveValue(value);
                  }}
                  size="extra-small"
                />
              </Chip>
            ))
          ) : (
            <span className="ads:text-primary text-14 text-gray-12">{placeholder}</span>
          )}
        </ComboboxInputTrigger>
        <ComboboxContent
          className="max-h-[min(320px,var(--radix-popover-content-available-height))] w-[var(--radix-popover-trigger-width)]"
          sideOffset={4}
          shouldFilter={false}
        >
          <ComboboxInput onValueChange={onValueChange} placeholder="Search..." value={searchValue} />

          {isLoading ? <ComboboxLoading>Loading...</ComboboxLoading> : <ComboboxEmpty>No results found.</ComboboxEmpty>}

          <ComboboxList>
            {values.map((value) => {
              const isSelected = selectedValues.some((val) => val === value);

              return (
                // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
                <ComboboxItem key={value} onSelect={() => onValueClick(value)}>
                  {isSelected ? (
                    <CheckIcon className="shrink-0 ads:text-icon-primary text-blue-9" size="sm" />
                  ) : (
                    <div className="size-4 shrink-0" />
                  )}
                  <div className={cn('truncate', isSelected ? 'opacity-70' : '')}>{value}</div>
                </ComboboxItem>
              );
            })}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    );
  },
);

StringSelect.displayName = 'StringSelect';
